/**
 * @description Contains all English translations for legal pages (legal notice, privacy policy, terms of service) and the help section.
 */

export const legalNotice = {
  title: "Legal Notice",
  subtitle: "Information according to § 5 DDG",
  projectTitle: "Website Operator",
  projectGroup: "University of Passau",
  university:
    "Faculty of Computer Science and Mathematics\nInstitute for Data Science\nInnstraße 43, 94032 Passau, Germany",
  representativeTitle: "Represented by",
  representativeName: "Max Mustermann",
  representativeRole: "(Technical Lead / Student Representative)",
  contactTitle: "Contact & Address",
  address:
    "University of Passau\nFaculty of Computer Science and Mathematics\nInnstraße 43, 94032 Passau, Germany",
  website: "https://www.fim.uni-passau.de/data-science",
  email: "webkurator@fim.uni-passau.de",
  phone: "+49(0)851 509-3306",
  projectDisclaimerTitle: "Important Note on Project Status",
  projectDisclaimerText: "This website started as a student project in the 'Development Practicum' at the University of Passau and is being continued beyond the practicum. It remains a non-commercial offer and is not an official offer of the university. The contents are for educational and demonstration purposes only.",
  editorTitle: "Technical Lead",
  editorName: "Ben Böck",
  editorAddress:
    "University of Passau\nOriginally developed by Development Practicum Winter Term 2025 / Group 2 at the Institute for Data Science, University of Passau.",
  disclaimer: "Disclaimer",
  disclaimerContent: "Liability for Content",
  disclaimerContentText:
    "The contents of our pages have been created with the utmost care. However, we cannot guarantee the contents' accuracy, completeness, or topicality. According to statutory provisions, we are furthermore responsible for our own content on these web pages according to § 7 Para.1 DDG.",
  disclaimerLinks: "Liability for Links",
  disclaimerLinksText: "Our offer contains links to external third-party websites over whose content we have no influence. Therefore, we cannot assume any liability for these external contents. The respective provider or operator of the pages is always responsible for the content of the linked pages. A warning is shown when leaving WebKurator.de because we cannot fully verify every URL entered on the platform.",
  disclaimerCopyright: "Copyright",
  disclaimerCopyrightText: "The content and works compiled by the site operators on these pages are subject to German copyright law. Downloads and copies of this page are only permitted for private, non-commercial use.",
};

export const privacy = {
  title: "Privacy Policy",
  subtitle: "Information on the processing of your personal data",
  introTitle: "General Information on Privacy",
  introText: "The WebKurator project group takes the protection of your personal data very seriously. We have implemented technical and organizational measures to ensure that data protection regulations are respected. As this is a student project, we limit data collection to what is technically necessary.",
  section1Title: "1. Provision of the Website & Technology",
  section1Text: "Data necessary for provision and security is automatically collected each time you access our offer. This includes IP address (anonymized), browser type, operating system, and access time. We also use your browser's 'Local Storage' to save your settings (e.g., dark mode) locally. This data is not merged with other data sources.",
  section2Title: "2. User Account & Profile Pictures",
  section2Text: "Registration is required for active participation (adding websites). We store your username, email address, and password (encrypted). Optionally, you can upload a profile picture. This image is stored on our servers and is visible to other users in connection with your activities (e.g., entries or comments). You can change or delete the image at any time in your profile settings.",
  section3Title: "3. Links to External Offers",
  section3Text: "Our offer contains links to external third-party websites (e.g., archived websites). We have no influence on whether these providers comply with data protection regulations. We accept no liability for the content and privacy practices of linked pages. Please inform yourself in the privacy policies of the respective providers.",
  section4Title: "4. Data Security & Encryption",
  section4Text: "This site uses SSL or TLS encryption for security reasons and to protect the transmission of confidential content, such as your registration data. Passwords are stored hashed according to current security standards (bcrypt) and are not visible in plain text to administrators.",
  section5Title: "5. Your Rights as a Data Subject",
  section5Text: "You have the right to free information about your stored personal data, its origin and recipients and the purpose of data processing, as well as a right to correction, blocking, or deletion of this data at any time. If you are logged in, you can download your data and request account deletion in My Account > Data & Privacy. Please contact the address given in the legal notice for additional requests.",
  section6Title: "6. Responsible Body",
  section6Text: "The responsible body for data processing on this website within the meaning of the General Data Protection Regulation (GDPR) is the project management:",
  contactName: "Project WebKurator (University of Passau)",
  contactUni: "Development Practicum Group 2",
  contactMail: "webkurator@fim.uni-passau.de"
};

export const terms = {
  title: "Terms of Service",
  subtitle: "Rules for using WebKurator (based on Curlie/DMOZ policies)",

  // 1. Acceptance
  scope: "1. Acceptance of Terms",
  scopeText1: "By using WebKurator in any way you agree to these terms. These Terms of Service ('TOS') are a contract between you and the 'WebKurator' project group at the University of Passau.",
  scopeText2: "If you cannot or will not follow the provisions in the TOS, you agree not to use WebKurator. WebKurator is a student project; we do not ensure that the service complies with local laws in countries other than Germany.",

  // 2. User Groups
  userGroups: "2. Access Rights & Roles",
  anonymousUsers: "Anonymous Users",
  anonymousRight1: "May browse the directory.",
  anonymousRight2: "May suggest sites for inclusion.",
  anonymousRight3: "Have no guarantee that their suggestions will be published.",

  registeredUsers: "Curators (Editors)",
  registeredRight1: "Can directly add websites to the directory.",
  registeredRight2: "Are responsible for the accuracy of their contributions.",
  registeredRight3: "Agree to the logging of their activities.",

  // 3. Account
  registrationAccount: "3. User Account",
  registrationIntro: "An account is required for active participation:",
  regEmail: "Valid email address",
  regUsername: "Username (publicly visible)",
  regPassword: "Secure password",
  registrationText1: "You are solely responsible for all activities that occur under your account. As this is a testing environment, please do not use passwords that you use for critical services.",
  registrationText2: "We reserve the right to terminate accounts at any time without notice (Virtual House Rights).",

  // 4. Responsibilities
  userObligations: "4. Your Responsibilities",
  userObligationsIntro: "You are responsible for your own activities when using WebKurator. You agree:",
  obligation1: "Not to disrupt the functioning of WebKurator.",
  obligation2: "Not to facilitate illegal activities (e.g., linking to illegal content).",
  obligation3: "Not to use automated systems (bots) without permission.",
  obligation4: "To accept sole responsibility for the content you post or suggest.",

  // 5. Discretion
  terminationTitle: "5. Discretion Over Content & Operation",
  terminationText: "We may edit, move, or delete any content in the Directory (including content that you have provided) at any time without notice or liability at our sole discretion. You acknowledge that WebKurator has unfettered editorial discretion to determine the structure and content of the Directory. You may not rely on any aspect of a site's inclusion in the Directory.",

  // 6. Data Quality
  dataQuality: "6. Data Quality",
  dataQualityText1: "WebKurator is provided on an 'AS IS' basis.",
  dataQualityText2: "We make no representations as to the accuracy, quality, timeliness, or completeness of the information available. Do not rely solely on this data for critical research.",

  // 7. Liability
  liability: "7. Disclaimer / Limitation of Liability",
  liabilityText1: "YOU USE THE SERVICE AT YOUR OWN RISK. WebKurator and the project group are not liable for errors or omissions in content or for any loss or damage resulting from use, unless based on intent or gross negligence.",
  liabilityText2: "We are not responsible for the content of linked third-party sites. You waive any claim resulting from your exposure to material on third-party sites.",

  // 8. Copyright Assignment
  copyright: "8. Copyright Assignment",
  copyrightText1: "To enable us to operate the directory, by submitting content (titles, descriptions), you grant WebKurator a non-exclusive, royalty-free right to use, copy, and adapt such material in the Directory.",
  copyrightText2: "You warrant that you have all rights necessary to authorize the distribution of the material you submit.",

  // 9. Copyright Claims
  copyrightClaims: "9. Claims of Copyright Infringement",
  copyrightClaimsText: "We respect the intellectual property rights of others. If you believe that your work has been copied and is accessible on WebKurator in a way that constitutes copyright infringement, please contact us at webkurator@fim.uni-passau.de. We reserve the right to terminate user access for repeat infringers.",

  // 10. Changes
  changes: "10. Changes to Terms",
  changesText: "We may modify these TOS at any time without notice. Your continued use of WebKurator after the effective date of any changes constitutes your agreement to the changes.",

  // 11. General
  jurisdiction: "11. General Terms",
  jurisdictionText: "The laws of the Federal Republic of Germany apply. The exclusive jurisdiction for any disputes is Passau. If any provision of this agreement is invalid, the remaining provisions remain in effect.",

  lastUpdated: "Last updated:",
  contactInfo: "Contact:"
};

export const help = {
  title: "Help & FAQ",
  subtitle: "Frequently asked questions and guides for using WebKurator",
  quickLinkSearch: "Using Search",
  quickLinkSearchDesc: "Learn how to search for websites effectively",
  quickLinkRegistration: "Registration",
  quickLinkRegistrationDesc: "Guide to creating a user account",
  quickLinkCategorization: "Categorization",
  quickLinkCategorizationDesc: "How to categorize websites",
  generalQuestions: "General Questions",
  q1: "What is WebKurator",
  a1: "WebKurator is a student project at the University of Passau for the systematic collection and categorization of websites. The platform allows users to jointly build a structured directory of web content.",
  q2: "Who can use WebKurator",
  a2: "The platform is accessible to everyone. Visitors can search and browse. Registered users can actively contribute, add and categorize websites. The project serves educational purposes.",
  q3: "Is it free to use",
  a3: "Yes, WebKurator is completely free to use. It is a non-commercial open-source project within a software development practicum.",
  q4: "What websites are included",
  a4: "The focus is on websites from and about Germany, but the community decides on the exact direction. The goal is a broad collection of thematically relevant content.",

  searchNavigation: "Search and Navigation",
  q5: "How do I use the search function",
  a5Intro: "Enter search terms in the search field. The search searches titles, descriptions and URLs of the registered websites.",
  a5Bullet1: "Use multiple search terms for more accurate results",
  a5Bullet2: "Use filters to narrow down by categories or regions",
  a5Bullet3: "Sort results by relevance, date or alphabetically",
  q6: "How do I search by categories",
  a6: 'Navigate to the "Categories" page via the main menu. There you can choose between thematic and regional categorization. Click on a category to display all associated websites.',
  q7: "What do the different sorting options mean",
  a7Sort1: "Relevance:",
  a7Sort1Desc: "Sorted by match with your search query",
  a7Sort2: "Newest first:",
  a7Sort2Desc: "Shows most recently added websites first",
  a7Sort3: "Oldest first:",
  a7Sort3Desc: "Shows longest archived websites first",
  a7Sort4: "A-Z / Z-A:",
  a7Sort4Desc: "Alphabetical sorting by title",

  registrationAndAccount: "Registration and Account",
  q8: "How do I register",
  a8Step1: 'Click "Register" in the main menu',
  a8Step2: "Enter username, email and a secure password",
  a8Step3: "Confirm your email address via the sent link",
  a8Step4: "Log in with your credentials",
  q9: "What are the password requirements",
  a9Intro: "A secure password must meet the following criteria:",
  a9Req1: "At least 8 characters long",
  a9Req2: "At least one uppercase letter",
  a9Req3: "At least one lowercase letter",
  a9Req4: "At least one digit",
  a9Req5: "At least one special character",
  q10: "I forgot my password",
  a10: 'Click "Forgot password" on the login page and enter your email address. You will receive a link to reset your password via email.',
  q11: "How do I change my account information",
  a11: 'After logging in, you can find the option to change email address, username and password under "My Account" in the user menu. Changes to the email address must be verified via a confirmation link.',

  addingWebsites: "Adding and Editing Websites",
  q12: "How do I add a new website",
  a12Intro: "As a registered user, you can add new websites:",
  a12Step1: "Log in",
  a12Step2: 'Click "Add Website"',
  a12Step3: "Enter title, URL and description",
  a12Step4: "Select appropriate categories",
  a12Step5: "Save the entry",
  q13: "How do I categorize a website",
  a13: "When categorizing, you can assign thematic and regional categories. The system makes automatic suggestions based on the content of the website (Machine Learning). You can accept these suggestions or select your own categories.",
  q14: "Can I edit entries from other users",
  a14: "Yes, registered users can edit and categorize all entries. This promotes collaborative quality assurance. All changes are logged.",
  q15: "What happens to my suggestions as an anonymous user",
  a15: "Suggestions from anonymous users are reviewed by registered users. They can accept, modify or reject the suggestion. You will not receive notification of the decision.",
  needMoreHelp: "Need More Help",
  needMoreHelpDesc: "If you can't find your question here, contact us",
  supportTeam: "Our support team is happy to help:",
  viewContactInfo: "View contact information",
};




